import { Injectable } from '@angular/core';
import { Badge } from '../models/badge.model';
import { SnackbarService } from './snackbar.service';

@Injectable({ providedIn: 'root' })
export class ShareCardsService {
  constructor(private snackbar: SnackbarService) {}

  shareMatchDay(summary: any): Promise<void> {
    const podium = (summary?.data?.topMovers ?? []).slice(0, 3);
    return this.shareCard({
      fileName: `resultado-${summary?.dayId || 'jornada'}.png`,
      eyebrow: summary?.title || 'Resultado de jornada',
      title: podium[0]?.userName || 'Jornada completada',
      subtitle: `${podium[0]?.roundPoints ?? 0} puntos en la jornada`,
      rows: podium.map(
        (entry: any, index: number) =>
          `${index + 1}. ${entry.userName} · ${entry.roundPoints} pts`,
      ),
      accent: '#f7c948',
    });
  }

  shareBadge(badge: Badge, userName?: string): Promise<void> {
    return this.shareCard({
      fileName: `insignia-${badge.id}.png`,
      eyebrow: 'Nueva insignia',
      title: `${badge.icon} ${badge.title}`,
      subtitle: userName
        ? `${userName} ha desbloqueado esta insignia`
        : badge.description,
      rows: [badge.description, badge.tier.toUpperCase()],
      accent: badge.tier === 'gold' ? '#f7c948' : '#7c9dff',
    });
  }

  shareGroupPosition(params: {
    groupName: string;
    userName: string;
    position: number;
    points: number;
  }): Promise<void> {
    return this.shareCard({
      fileName: `posicion-${params.groupName}.png`,
      eyebrow: params.groupName,
      title: `${params.position}ª posición`,
      subtitle: `${params.userName} · ${params.points} puntos`,
      rows: ['Clasificación del grupo', 'Mundial 2026'],
      accent: '#27c58f',
    });
  }

  private async shareCard(config: {
    fileName: string;
    eyebrow: string;
    title: string;
    subtitle: string;
    rows: string[];
    accent: string;
  }): Promise<void> {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1080;
    const context = canvas.getContext('2d');
    if (!context) return;

    const gradient = context.createLinearGradient(0, 0, 1080, 1080);
    gradient.addColorStop(0, '#07142e');
    gradient.addColorStop(1, '#182d62');
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = config.accent;
    context.fillRect(72, 72, 16, 936);
    context.fillStyle = 'rgba(255,255,255,.08)';
    context.beginPath();
    context.arc(920, 170, 240, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = config.accent;
    context.font = '700 34px Arial';
    context.fillText(config.eyebrow.toUpperCase(), 130, 190);
    context.fillStyle = '#ffffff';
    context.font = '800 76px Arial';
    this.drawWrappedText(context, config.title, 130, 320, 820, 90);
    context.fillStyle = 'rgba(255,255,255,.78)';
    context.font = '500 38px Arial';
    this.drawWrappedText(context, config.subtitle, 130, 500, 820, 52);

    context.font = '700 36px Arial';
    context.fillStyle = '#ffffff';
    config.rows.slice(0, 4).forEach((row, index) => {
      context.fillStyle = index === 0 ? config.accent : '#ffffff';
      context.fillText(row, 130, 700 + index * 70);
    });

    context.fillStyle = 'rgba(255,255,255,.55)';
    context.font = '700 28px Arial';
    context.fillText('WC 2026 · PORRA', 130, 985);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png'),
    );
    if (!blob) return;

    const file = new File([blob], config.fileName, { type: 'image/png' });
    if (
      navigator.share &&
      (!navigator.canShare || navigator.canShare({ files: [file] }))
    ) {
      await navigator.share({ files: [file], title: config.eyebrow });
      return;
    }

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = config.fileName;
    link.click();
    URL.revokeObjectURL(link.href);
    this.snackbar.info('Tarjeta descargada para compartir.');
  }

  private drawWrappedText(
    context: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
  ): void {
    const words = text.split(' ');
    let line = '';
    let lineIndex = 0;

    for (const word of words) {
      const candidate = `${line}${word} `;
      if (context.measureText(candidate).width > maxWidth && line) {
        context.fillText(line.trim(), x, y + lineIndex * lineHeight);
        line = `${word} `;
        lineIndex++;
      } else {
        line = candidate;
      }
    }
    context.fillText(line.trim(), x, y + lineIndex * lineHeight);
  }
}
